/**
 * Writes boards to the server, and keeps the screen's boards in step with the ["dashboards"] cache (#1675).
 *
 * The store used to fire a debounced PATCH and ignore its answer: a failure was swallowed, nothing told the React Query
 * cache what the server now held, and the sync from that cache guarded only the 700 ms before the PATCH went out, so a
 * refetch that was already under way could land after the edit and put the old board back.
 *
 * This keeps all of it in one framework-free place (the store holds one):
 *  - schedule(): the debounced autosave of a drag or an edit. A failure is told to onAutosaveError once per run of
 *    failures, never swallowed.
 *  - save(): the Save button. It sends the newest copy now, waits for the answer and throws when the write fails.
 *  - add() and forget(): a board created, cloned or deleted on the screen. The cache is kept in step with them, because
 *    every answered write updates the cache and the screen then syncs from it: a cache that did not know a created board
 *    would drop it from the list, and one that still held a deleted board would bring it back.
 *  - Writes to one board go out one at a time, in order, so the server never applies an older one last.
 *  - Every answered write is put into the cache (the PATCH's response), and a list read that began before the write is
 *    cancelled so it cannot answer with the board as it was.
 *  - syncGuard() + mergeServerBoards(): what the sync from the cache must leave alone, per board. A board holds its local
 *    copy from the first edit until the server has answered it, and for good after a failed write; a read that is older
 *    than the board's last answered write (it began while that write was out) is ignored for that board.
 */

import type { QueryClient } from "@tanstack/react-query";
import type { Dashboard, DashboardList } from "@/lib/api/types";
import type { DashboardConfig } from "./types";

export const DASHBOARDS_KEY = ["dashboards"] as const;

/** How long after the last change the autosave writes. Drags and edits fire fast; one write per pause. */
export const AUTOSAVE_MS = 700;

/** The list with `saved` in place of its older copy, or first when the list does not hold it yet. No list: untouched. */
export function withSavedBoard(list: DashboardList | undefined, saved: Dashboard): DashboardList | undefined {
  if (!list) return list;
  const held = list.items.some((d) => d.id === saved.id);
  return {
    ...list,
    items: held ? list.items.map((d) => (d.id === saved.id ? saved : d)) : [saved, ...list.items],
    total: held ? list.total : list.total + 1,
  };
}

/** The list without this board. No list: untouched. */
export function withoutBoard(list: DashboardList | undefined, id: string): DashboardList | undefined {
  if (!list || !list.items.some((d) => d.id === id)) return list;
  return { ...list, items: list.items.filter((d) => d.id !== id), total: Math.max(0, list.total - 1) };
}

/** A board that is not on the server yet, as the list holds boards: the whole config under the envelope. */
export function localEnvelope(dash: DashboardConfig, list?: DashboardList): Dashboard {
  return {
    id: dash.id,
    org_id: list?.items[0]?.org_id ?? "",
    slug: dash.id,
    name: dash.name,
    config: dash,
    created_at: dash.updatedAt,
    updated_at: dash.updatedAt,
  };
}

/** What the sync from the cache leaves alone (BoardWriter.syncGuard), by board id. */
export interface SyncGuard {
  /** Boards whose local copy is the newest: an edit waiting to go out, a write out, or a write that failed. */
  held: ReadonlySet<string>;
  /** Boards deleted here: a read that still lists one must not bring it back. */
  gone: ReadonlySet<string>;
  /** The server's updated_at of each board's last answered write. A read older than it began before that write. */
  savedAt: ReadonlyMap<string, string>;
}

/**
 * The screen's boards after the server's list arrives: the server's boards in the server's order, except that a board
 * the guard holds keeps its local copy (and a held board the list lacks, such as a create still out, stays), a board
 * the guard knows to be deleted stays out, and a board whose server copy is older than this client's last answered
 * write to it keeps its local copy. Other boards take the server's.
 */
export function mergeServerBoards(
  local: readonly DashboardConfig[],
  server: readonly DashboardConfig[],
  guard: SyncGuard,
): DashboardConfig[] {
  const mine = new Map(local.map((d) => [d.id, d]));
  const listed = new Set(server.map((d) => d.id));
  const merged: DashboardConfig[] = [];
  for (const theirs of server) {
    if (guard.gone.has(theirs.id)) continue;
    const own = mine.get(theirs.id);
    const saved = guard.savedAt.get(theirs.id);
    const staleRead = saved !== undefined && Date.parse(theirs.updatedAt) < Date.parse(saved);
    merged.push(own && (guard.held.has(theirs.id) || staleRead) ? own : theirs);
  }
  const notListedYet = local.filter((d) => guard.held.has(d.id) && !listed.has(d.id) && !guard.gone.has(d.id));
  return [...notListedYet, ...merged];
}

export interface BoardWriterDeps {
  queryClient: QueryClient;
  /** The PATCH: the name and the whole config blob of one board. Resolves to the board as the server saved it. */
  update: (id: string, body: { name: string; config: unknown }) => Promise<Dashboard>;
  /** The POST of a board the screen minted the id of (the server upserts on it). */
  create: (body: { id: string; name: string; config: unknown }) => Promise<Dashboard>;
  /** The DELETE (idempotent on the server). */
  remove: (id: string) => Promise<void>;
  /** An autosave failed. Called once for a run of failures; the next write that lands starts a new run. */
  onAutosaveError: (err: unknown) => void;
  debounceMs?: number;
}

const noop = () => {};

export class BoardWriter {
  private readonly timers = new Map<string, ReturnType<typeof setTimeout>>();
  /** The newest copy of each board whose autosave has not gone out yet. */
  private readonly waiting = new Map<string, DashboardConfig>();
  /** Writes queued or out, per board. */
  private readonly pending = new Map<string, number>();
  /** The tail of each board's queue of writes. It never rejects. */
  private readonly queues = new Map<string, Promise<void>>();
  /** Boards whose last write failed: their local copy is the only one that holds the edit. */
  private readonly failed = new Set<string>();
  private readonly gone = new Set<string>();
  private readonly savedAt = new Map<string, string>();
  private autosaveFailing = false;
  /** Counts the cancels of each board, so a write that was queued or out when it was cancelled can tell. */
  private readonly cancels = new Map<string, number>();
  private readonly deps: BoardWriterDeps;

  constructor(deps: BoardWriterDeps) {
    this.deps = deps;
  }

  /** What the sync from the cache must leave alone now, per board (mergeServerBoards). */
  syncGuard(): SyncGuard {
    const held = new Set<string>([...this.timers.keys(), ...this.failed]);
    for (const [id, n] of this.pending) if (n > 0) held.add(id);
    return { held, gone: new Set(this.gone), savedAt: new Map(this.savedAt) };
  }

  /** Autosave this board once its changes pause. A newer call for the same board replaces the one waiting. */
  schedule(dash: DashboardConfig): void {
    const timer = this.timers.get(dash.id);
    if (timer) clearTimeout(timer);
    this.waiting.set(dash.id, dash);
    this.timers.set(
      dash.id,
      setTimeout(() => {
        this.timers.delete(dash.id);
        this.waiting.delete(dash.id);
        this.send(dash).catch((err: unknown) => {
          if (this.autosaveFailing) return;
          this.autosaveFailing = true;
          this.deps.onAutosaveError(err);
        });
      }, this.deps.debounceMs ?? AUTOSAVE_MS),
    );
  }

  /** Drop whatever is waiting for this board, and make a write that is queued or out for it a no-op. */
  cancel(id: string): void {
    const timer = this.timers.get(id);
    if (timer) clearTimeout(timer);
    this.timers.delete(id);
    this.waiting.delete(id);
    this.failed.delete(id);
    this.cancels.set(id, (this.cancels.get(id) ?? 0) + 1);
  }

  /**
   * Write this board now and wait for the server. `current` is the board as the screen holds it; an autosave still
   * waiting has a newer copy only when the screen's own has not caught up, so that one wins. An autosave already out
   * is waited for first (the writes to a board go in order), and one still queued is dropped for this newer copy.
   * Throws when the write fails: the edit stays in the local board, and a later write retries it.
   */
  async save(current: DashboardConfig): Promise<void> {
    const dash = this.waiting.get(current.id) ?? current;
    this.cancel(current.id);
    await this.send(dash);
  }

  /** A board created or cloned on the screen: into the cache, then to the server. A failed POST leaves it local only. */
  add(dash: DashboardConfig): void {
    const { queryClient, create } = this.deps;
    this.enqueue(dash.id, async () => {
      await this.dropEarlierReads();
      queryClient.setQueryData<DashboardList>(DASHBOARDS_KEY, (list) => withSavedBoard(list, localEnvelope(dash, list)));
      const saved = await create({ id: dash.id, name: dash.name, config: dash });
      this.noteAnswer(saved);
      // The id is the screen's own, so the server keeps it; were it to answer with another, the list follows the server.
      queryClient.setQueryData<DashboardList>(DASHBOARDS_KEY, (list) =>
        withSavedBoard(saved.id === dash.id ? list : withoutBoard(list, dash.id), saved),
      );
    }).catch(noop);
  }

  /** A board deleted on the screen: out of the cache, then off the server. No write to it goes out after this. */
  forget(id: string): void {
    const { queryClient, remove } = this.deps;
    this.cancel(id);
    this.gone.add(id);
    this.enqueue(id, async () => {
      await this.dropEarlierReads();
      queryClient.setQueryData<DashboardList>(DASHBOARDS_KEY, (list) => withoutBoard(list, id));
      await remove(id);
    }).catch(() => {
      // The board is still on the server: let the next read bring it back.
      this.gone.delete(id);
    });
  }

  private send(dash: DashboardConfig): Promise<void> {
    const { queryClient, update } = this.deps;
    const id = dash.id;
    const cancelsAtStart = this.cancels.get(id) ?? 0;
    // A cancel (the board was deleted, or Save sent a newer copy) after this write was queued makes its answer moot.
    const cancelled = () => (this.cancels.get(id) ?? 0) !== cancelsAtStart;
    return this.enqueue(id, async () => {
      if (cancelled()) return;
      try {
        // A list read that began before this write would answer with the board as it was; drop it.
        await this.dropEarlierReads();
        const saved = await update(id, { name: dash.name, config: dash });
        this.failed.delete(id);
        this.autosaveFailing = false;
        this.noteAnswer(saved);
        if (!cancelled()) {
          queryClient.setQueryData<DashboardList>(DASHBOARDS_KEY, (list) => withSavedBoard(list, saved));
        }
      } catch (err) {
        // Nothing to tell about a board that is gone, or a copy a newer write replaced.
        if (cancelled()) return;
        this.failed.add(id);
        throw err;
      }
    });
  }

  /**
   * Cancel a list read that began before the write about to go out: it would answer with the boards as they were. Only a
   * list that has been read once, since cancelling the very first read would leave the list unread.
   */
  private async dropEarlierReads(): Promise<void> {
    const { queryClient } = this.deps;
    if (queryClient.getQueryData(DASHBOARDS_KEY) === undefined) return;
    await queryClient.cancelQueries({ queryKey: DASHBOARDS_KEY });
  }

  /** Remember how new the server's copy of this board now is (its own clock, so no skew with this one). */
  private noteAnswer(saved: Dashboard): void {
    const before = this.savedAt.get(saved.id);
    if (before === undefined || Date.parse(saved.updated_at) >= Date.parse(before)) {
      this.savedAt.set(saved.id, saved.updated_at);
    }
  }

  /** Run a write to this board after the ones before it, holding the board's local copy from now until it is done. */
  private enqueue(id: string, job: () => Promise<void>): Promise<void> {
    this.pending.set(id, (this.pending.get(id) ?? 0) + 1);
    const run = (this.queues.get(id) ?? Promise.resolve()).then(job).finally(() => {
      const left = (this.pending.get(id) ?? 1) - 1;
      if (left > 0) this.pending.set(id, left);
      else this.pending.delete(id);
    });
    this.queues.set(id, run.then(noop, noop));
    return run;
  }
}
